import { FetchResult } from '@apollo/client'
import {
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Button, Icons, InformationModal, SkeletonText } from 'utoniq-ui'

import { UserCartsConnectionFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, Template } from '@/core/architecture'
export * from './fragments'

export type State = {
  loading: boolean
  deleting: boolean
  carts: UserCartsConnectionFragment | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onDeleteCart: (cartId: string) => Promise<FetchResult>
}

export const StoreCartListTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onDeleteCart,
}) => {
  const { t } = useTranslation('storeCartList')
  const { routerPages, generatePath, routerNames } = useRouter()
  const { analytics } = useAnalytics()
  // カート削除用state
  const [cart, setCart] = useState<
    UserCartsConnectionFragment['edges'][number]['node'] | null
  >(null)

  return (
    <BaseLayout header={{ back: true, title: t('title') }}>
      <Head title={routerNames.StoreCartList} />
      {state.loading ? (
        <Stack sx={{ px: 5, py: 2 }}>
          <SkeletonText variant="large" width="50%" />
        </Stack>
      ) : (
        <Typography variant="h3" sx={{ px: 5, py: 2 }}>
          {t('cart')}
        </Typography>
      )}
      <ScrollFetcher
        component={List}
        nodes={state.carts?.edges.map(({ node }) => (
          <ListItem
            disablePadding
            secondaryAction={
              <IconButton
                size="small"
                edge="end"
                sx={{ bgcolor: 'grey.100' }}
                onClick={() => setCart(node)}
              >
                <Icons.Trash fontSize="xSmall" />
              </IconButton>
            }
          >
            <ListItemButton
              divider
              href={generatePath(routerPages.StoreCartDetail, {
                cartId: node.id,
              })}
              onClick={() => {
                analytics?.track('click_select', {
                  applicationKey: config.applicationKey,
                  artistId: node.artist.id,
                  name: 'cartList',
                })
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={node?.artist?.avatar?.smallUri ?? ''}
                  sx={{ width: 40, height: 40 }}
                />
              </ListItemAvatar>
              <ListItemText
                primary={node.artist.displayName}
                primaryTypographyProps={{ sx: styleSets.lineCamp(1) }}
                secondary={
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="caption">
                      {t('itemNumber', { number: node.totalCount })}
                    </Typography>
                    <Typography variant="subtitle2">
                      ¥{node.formattedTotalPrice}
                    </Typography>
                  </Stack>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
        emptyMessage={t('cartEmptyMessage', 'カート内に商品はありません。')}
        loadingComponent={
          <List>
            {Array.from(Array(3).keys()).map(key => (
              <CartItemSkeleton key={key} />
            ))}
          </List>
        }
        loading={state.loading}
        hasNextPage={false}
        onFetchMore={onFetchMore}
      />
      <InformationModal
        open={!!cart}
        onClose={() => setCart(null)}
        title={t('trashConfirmTitle', { title: cart?.artist.displayName })}
        content=""
        cancelText={t('close', '閉じる')}
        buttons={
          <Button
            sx={{ minWidth: 212 }}
            size="large"
            onClick={() => {
              if (!cart) return
              analytics?.track('click_deleteCart', {
                applicationKey: config.applicationKey,
                artistId: cart.artist.id,
              })

              onDeleteCart(cart?.id).finally(() => {
                setCart(null)
              })
            }}
            loading={state.deleting}
          >
            {t('remove', '削除する')}
          </Button>
        }
      />
    </BaseLayout>
  )
}

const CartItemSkeleton: Cell = () => (
  <ListItem
    disablePadding
    secondaryAction={
      <Skeleton variant="circular" sx={{ width: 24, height: 24 }} />
    }
  >
    <ListItemButton divider>
      <ListItemAvatar>
        <Skeleton variant="circular" sx={{ width: 40, height: 40 }} />
      </ListItemAvatar>
      <ListItemText
        primary={<SkeletonText variant="h3" width="75%" />}
        secondary={
          <Stack direction="row" spacing={2} alignItems="center">
            <SkeletonText variant="h4" width="30%" />
            <SkeletonText variant="h4" width="30%" />
          </Stack>
        }
      />
    </ListItemButton>
  </ListItem>
)
